import { Icon } from "@iconify/react";
import { AdminPanelSettings, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios, { AxiosResponse } from "axios";
import dayjs, { duration } from "dayjs";
import {
  inRange,
  isEmpty,
  isUndefined,
  last,
  minBy,
  toNumber,
  uniqBy,
  upperCase,
} from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useBeforeUnload,
  useBlocker,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import Banner from "../../components/admin/Banner";
import Header from "../../components/dashboard/Header";
import { ChukkersTable } from "../../components/exams/ChukkersTable";
import DataSuccessMessage from "../../components/exams/DataSuccessMessage";
import EcgActionsModal from "../../components/exams/EcgActionsModal";
import GaitAndHrZone from "../../components/exams/GaitAndHrZone";
import InformationSection from "../../components/exams/InformationSection";
import KeyParamSection from "../../components/exams/KeyParamSection";
import MapSection from "../../components/exams/MapSection";
import PostProcess from "../../components/exams/PostProcess";
import SplitButton from "../../components/exams/SplitButton";
import TabsGraphs from "../../components/exams/TabsGraphs";
import Hideable from "../../components/global/Hideable";
import NotFound from "../../components/global/NotFound";
import Page from "../../components/layout/Helmet";
import TrackViewSkeleton from "../../components/tracks/TrackViewSkeleton";
import { CreateChukkerDto } from "../../dto/createChukkerDto";
import { useAuth } from "../../hooks/useAuth";
import { useExam } from "../../hooks/useExam";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";
import { Chukkers } from "../../shared/types/chukkers.type";
import { CheckPoint } from "../../shared/types/training.type";
import {
  MapColorSpeedConfig,
  TrainingSetting,
} from "../../shared/types/trainingSetting.type";
import { TTransposedTraining } from "../../shared/types/transposedTraining.type";
import { convertSpeed } from "../../shared/utils/conversion";
import { formatDate, getCorrectDateFormat } from "../../shared/utils/DateUtils";
import { getTime, startTimeFrom0 } from "../../shared/utils/MapSection";
import { RootState } from "../../store";
import {
  toggleDispatchSaveChukker,
  toggleIsChukkerEditedBannerVisible,
} from "../../store/reducers/display.reducer";
import {
  setAddVetModal,
  setAddVetModalExam,
  setBlockedEditModal,
  setEditTrainingModal,
  setTrainingToEdit,
} from "../../store/reducers/modal.reducer";
import { setRestrictedMode, setUser } from "../../store/reducers/user.reducer";
import { routes } from "../routes";
import { useLocales } from "../../hooks/useLocales";
dayjs.extend(duration);

declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (handlerName: string, ...args: any[]) => Promise<any>;
    };
  }
}
export const CALL_HANDLER_KEY = "synchroAppHandler";

export type Scale = "DISTANCE" | "TIME";

function ExamView() {
  const { t, i18n } = useTranslation();
  const { currentLang } = useLocales();
  let { id } = useParams();
  const [showBanner, setShowBanner] = useState(false);
  const [scaleOption, setScaleOption] = useState<Scale>("DISTANCE");
  const [distance, setDistance] = useState<string>("0 mi");
  const [speed, setSpeed] = useState<number[]>([]);
  const [bpm, setBpm] = useState<number[]>([]);
  const [loCardio, setLoCardio] = useState<number[]>([]);
  const [time, setTime] = useState<number[]>([]);
  const [rawDistance, setDistanceRaw] = useState<number[]>([]);
  const [strideFrequency, setStrideFrequency] = useState<number[]>([]);
  const [strideLength, setStrideLength] = useState<number[]>([]);
  const [currentIndex, setCurrentindex] = useState(0);
  const [lats, setLats] = useState<number[]>([]);
  const [lngs, setLangs] = useState<number[]>([]);
  const [allure, setAllure] = useState<number[]>([]);
  const {
    exam,
    isExamLoading,
    examHasError,
    examQuery,
    downloadExamReport,
    downloadEcg,
    getEqmTitle,
    isEcgDownloading,
    isPdfDownloading,
    exportExamPage,
    exportXlsxData,
    isExamExporting,
    processExamMutation,
    saveExamChukker,
    isChukkersLoading,
  } = useExam(false, null, toNumber(id));
  const isFinishLineActive = exam?.training?.use_finish_line ?? false;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const userSettings = useSelector<RootState>(
    (state) => state.user.user?.training_settings,
  ) as TrainingSetting;
  const initialSpeedUnit = useSelector<RootState>(
    (state) => state.user.initialSpeedUnit,
  ) as string;
  const restricted = useSelector<RootState>(
    (state) => state.user.restrictedMode,
  ) as boolean;
  const isMapLoaded = useSelector<RootState>(
    (state) => state.display.mapLoaded,
  ) as boolean;
  const initialSysUnit = useSelector<RootState>(
    (state) => state.user.initialUnitSystem,
  ) as string;
  const transposedMutation = useMutation(
    (): Promise<AxiosResponse<TTransposedTraining>> =>
      axios.get(exam?.training?.moments_transposed_file_url as string),
  );
  const { isAdmin, auth, disconnect, isUserTypePolo } = useAuth();
  const [selectedEcgOptionIndex, setSelectedEcgOptionIndex] = React.useState(0);
  const mapColorType = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.map_colors_type,
  ) as "gait" | "speed";

  const [selectedPdfOptionIndex, setSelectedPdfOptionIndex] = React.useState(0);
  const mapSpeedConfig = useSelector<RootState>(
    (state) =>
      state.user.user?.training_settings?.map_colors_speed_configuration,
  ) as MapColorSpeedConfig;
  const [mapMode, setMapMode] = useState<"gait" | "speed">(mapColorType);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  //State for chukkers management
  const [plotBandId, setPlotBandId] = useState<number | null>(null);
  const [isChukkerSelectionEnabled, setIsChukkerSelectionEnabled] =
    React.useState(false);
  const [isChukkerEditionEnabled, setIsChukkerEditionEnabled] =
    React.useState(false);
  const [isChukkerEditModeEnabled, setIsChukkerEditModeEnabled] =
    React.useState(false);
  const [editActive, setIsEditActive] = React.useState(false);

  //End state for chukkers management

  const queryClient = useQueryClient();
  const { track } = useUserActivityLog();

  const [chukkers, setChukkers] = useState<Chukkers>([]);

  const SOURCE = searchParams.get("source") as undefined | "app-synchro";

  const finish_line =
    exam?.training?.finish_line ?? exam?.training?.finish_line_automatic;

  // const restrictedMode = restricted || SOURCE === 'app-synchro'
  const restrictedMode = restricted;

  const xlsxEnabled = useMemo(() => {
    if (!auth) return false;

    const XSLX_CONFIG_KEY = "ALLOW_XLSX_MOMENTS_EXPORT";

    const getConfig = auth.configurations.find(
      (conf) => conf.key === XSLX_CONFIG_KEY,
    );

    if (!getConfig) return false;

    return getConfig.value === "true";
  }, [auth]);

  const pdfOptions = [
    {
      key: "DOWNLOAD_PDF",
      title: t("report:buttonText"),
      enabled: true,
    },
    {
      key: "EXPORT_PDF",
      title: t("report:exportTraining"),
      enabled: true,
    },
    {
      key: "EXPORT_XLSX",
      title: t("report:exportXlsx"),
      enabled: xlsxEnabled,
    },
  ].filter((option) => !!option.enabled);

  const processEnabled = useMemo(() => {
    if (!auth) return false;

    const PP_CONFIG_KEY = "DISPLAY_PP_BUTTON_TRAINING_PAGE";

    const getConfig = auth.configurations.find(
      (conf) => conf.key === PP_CONFIG_KEY,
    );

    if (!getConfig) return false;

    return getConfig.value === "true";
  }, [auth]);

  const handleEdit = () => {
    exam && dispatch(setTrainingToEdit(exam));
    dispatch(setEditTrainingModal(true));
  };

  //Effects
  useEffect(() => {
    return () => clearState();
  }, []);

  useEffect(() => {
    if (exam) {
      if (exam?.training?.moments_transposed_file_url) {
        transposedMutation
          .mutateAsync()
          .then((res) => {
            let lts = sliceForRelevantMoment(res?.data.latitude ?? []);
            let lgs = sliceForRelevantMoment(res?.data.longitude ?? []);

            setLats(lts);
            setLangs(lgs);

            setSpeed(sliceForRelevantMoment(res?.data?.speed ?? []));
            setBpm(sliceForRelevantMoment(res?.data?.bpm ?? []));
            setDistanceRaw(sliceForRelevantMoment(res.data?.distance ?? []));
            setTime(sliceForRelevantMoment(res.data?.timestamp ?? []));
            setStrideFrequency(sliceForRelevantMoment(res.data?.pace ?? []));
            setStrideLength(sliceForRelevantMoment(res.data?.amplitude ?? []));
            setAllure(sliceForRelevantMoment(res.data?.allure ?? []));
            setLoCardio(
              sliceForRelevantMoment(
                res.data?.lo_cardio ?? res.data?.loCardio ?? [],
              ),
            );
          })
          .catch((err) => {
            console.log({ err });
            toast(t("error:fileNotFound"), { type: "error" });
          });
      } else {
        toast(t("error:fileNotFound"), { type: "error" });
      }
    }
  }, [
    exam?.training?.moments_transposed_file_url,
    exam?.training?.relevant_moments_index_start,
    exam?.training?.relevant_moments_index_end,
  ]);

  useEffect(() => {
    if (exam) {
      //If the training has no horse we display the edit modal and prevent the user from leaving it

      if (isUserTypePolo) {
        setChukkers(exam.training?.chukkers || []);
      }

      if (!exam?.training?.horse_trainer?.horse) {
        handleEdit();
        dispatch(setBlockedEditModal(true));
      }

      let examGoodGps = exam?.training?.percentage_good_gps;

      if (typeof examGoodGps === 'number' && Number.isFinite(examGoodGps) && examGoodGps < 20) {
  setScaleOption("TIME");
}
      if (!isExamLoading) {
        track("TRAINING_PAGE_DISPLAYED");
      }
    }
  }, [exam?.["@id"], isUserTypePolo, isExamLoading]);

  // Block navigating elsewhere when data has been entered into the input
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (
      nextLocation.pathname.includes("login") ||
      nextLocation.pathname.includes("logout")
    ) {
      disconnectUser();
      return true;
    }
    return restrictedMode && currentLocation.pathname !== nextLocation.pathname;
  });

  const clearState = () => {
    if (isAdmin) {
      //Reset inital values
      dispatch(setUser({ ...auth, default_speed_unit: initialSpeedUnit }));
      dispatch(
        setUser({
          ...auth,
          unit_system: initialSysUnit as "metric" | "imperial",
        }),
      );
    }
    dispatch(setBlockedEditModal(false));
    dispatch(toggleIsChukkerEditedBannerVisible(false));
    dispatch(toggleDispatchSaveChukker(false));
  };

  useBeforeUnload(() => clearState());

  const filteredCheckPoints = useMemo(() => {
    let cps = exam?.training?.checkpoints;
    if (cps && cps.length > 0) {
      let checkpoints = uniqBy(
        cps.map((cp) => cp.checkpoint),
        "@id",
      );

      let checkPointsMin: CheckPoint[] = [];
      checkpoints.forEach((cp) => {
        let getDataBydId =
          cps?.filter((c) => c?.checkpoint?.["@id"] === cp?.["@id"]) ?? [];

        let data = minBy(getDataBydId, "index_moment");

        data && checkPointsMin.push(data);
      });
      return checkPointsMin;
    } else {
      return [];
    }
  }, [exam?.training?.checkpoints]);

  const isExamHasEcg = useMemo(() => {
    return !isEmpty(exam?.training_ecg_data);
  }, [exam?.training_ecg_data]);

  const getPageTitle = () => {
    let horse =
      exam?.training?.horse_trainer?.horse?.name ??
      getEqmTitle(exam?.training?.equimetre);
    let userDateFormat = auth?.default_date_format
      ? getCorrectDateFormat(auth?.default_date_format)
      : "DD/MM/YYYY";
    let date = formatDate(exam?.training?.creation_date ?? "", userDateFormat);
    return [horse, date].join(" - ");
  };

  const reloadPostProcess = () => {
    examQuery.refetch();
  };

  const handleClose = () => {
    blocker?.reset?.();
  };

  const disconnectUser = () => {
    dispatch(setRestrictedMode(false));
    disconnect();
    blocker.proceed?.();
  };

  const sliceForRelevantMoment = (arr: number[]) => {
    let shouldSlice =
      exam?.training?.relevant_moments_index_start ||
      exam?.training?.relevant_moments_index_end;
    if (!shouldSlice) return arr;

    return arr.slice(
      exam?.training.relevant_moments_index_start ?? 0,
      exam?.training.relevant_moments_index_end ?? arr.length,
    );
  };

  const shareEcg = () => {
    dispatch(setAddVetModal(true));
    if (exam?.training?.id) {
      dispatch(setAddVetModalExam(exam.training.id));
    }
  };
  const requestAnalyse = () => {
    let link = t("ecg:requestAnalysis:formUrl").concat("?");
    if (auth) {
      const currentUrl = new URL(window.location.href);

      currentUrl.searchParams.delete("source");

      let queryParams: string[] = [];
      queryParams.push(
        `training_ecg_to_be_analysed=${encodeURI(currentUrl.toString())}`,
      );
      queryParams.push(`firstname=${auth.firstname}`);
      queryParams.push(`lastname=${auth.lastname}`);
      queryParams.push(`email=${auth.email}`);
      link += queryParams.join("&");
    }
    window.open(encodeURI(link), "_blank");
  };

  const handleEcgBtnClick = (key: string) => {
    switch (key) {
      case "VISUALISE_ECG":
        window.open(
          process.env.REACT_APP_DVET_FRONT_URL?.concat(
            `horses/eqm_${exam?.training?.horse_trainer?.horse?.id}/examequimetre/eqm_${id}`,
          ),
          "_blank",
        );
        break;
      case "SHARE_ECG":
        shareEcg();
        break;
      case "REQUEST_ANALYS":
        requestAnalyse();
        break;
      case "DOWNLOAD_ECG":
        handleDownloadWithAppSynchro("DOWNLOAD_ECG_CSV_BUTTON_CLICKED", () => {
          downloadEcg(exam?.training_ecg_data);
        });

        break;
      default:
        break;
    }
  };

  const handleDownloadWithAppSynchro = (
    eventKey: string,
    callBack: () => void,
  ) => {
    //If app loaded from web view send event to flutter app
    if (SOURCE === "app-synchro") {
      try {
        console.log(eventKey + ` triggered`);
        window.flutter_inappwebview?.callHandler(CALL_HANDLER_KEY, {
          type: eventKey,
        });
      } catch (e) {
        console.error(e);
      }
      return;
    }

    //Else run the current function
    callBack();
  };

  const handlePdfBtnClick = (key: string) => {
    switch (key) {
      case "DOWNLOAD_PDF":
        handleDownloadWithAppSynchro("DOWNLOAD_PDF_BUTTON_CLICKED", () => {
          downloadExamReport(toNumber(id));
        });
        track("TRAINING_PDF_BUTTON_CLICKED");
        break;
      case "EXPORT_PDF":
        handleDownloadWithAppSynchro("EXPORT_PDF_BUTTON_CLICKED", () => {
          exportExamPage(toNumber(id));
        });
        break;
      case "EXPORT_XLSX":
        handleDownloadWithAppSynchro("EXPORT_XLSX_BUTTON_CLICKED", () => {
          exportXlsxData([toNumber(id)]);
        });
        break;
      default:
        break;
    }
  };

  const getShareLink = useCallback(async () => {
    if (isEmpty(exam?.training?.training_sharing_tokens)) return;
    try {
      let url = exam?.training?.training_sharing_tokens.at(0)?.url_v2;
      if (!url) return;

      await navigator.clipboard.writeText(url);
      toast(t("linkCopiedToClipboard"), { type: "success" });
    } catch (error) {
      toast(t("error:generic"), { type: "error" });
    }
  }, [exam?.training?.training_sharing_tokens?.length]);

  const showShareLink = useMemo(() => {
    return !isEmpty(exam?.training?.training_sharing_tokens);
  }, [exam?.training?.training_sharing_tokens?.length]);

  const handleOptionPdfClick = (idx?: number) => {
    const index = isUndefined(idx) ? selectedPdfOptionIndex : idx;
    const key = pdfOptions[index].key;
    return key;
  };

  const processData = async () => {
    try {
      await processExamMutation.mutateAsync(toNumber(id));

      toast(t("process:success"), { type: "success" });
    } catch (error) {
      toast(t("error:generic"), { type: "error" });
    }
  };

  const isChukkerInRage = (start: number, end: number, chukkers: Chukkers) => {
    if (isEmpty(chukkers)) return false;

    return chukkers.some(
      (chukker) =>
        inRange(
          start,
          chukker.manual_index_start || chukker.index_start,
          chukker.manual_index_end || chukker.index_end,
        ) ||
        inRange(
          end,
          chukker.manual_index_start || chukker.index_start,
          chukker.manual_index_end || chukker.index_end,
        ),
    );
  };

  const updateExamChukkers = async (chukkers: any) => {
    toast(t("chukkers:successMessage"), { type: "success" });

    let response = await saveExamChukker(chukkers);

    setChukkers(response);
    await queryClient.refetchQueries(["exam", toNumber(id)]);
  };

  const saveChukker = useCallback(
    async (data: CreateChukkerDto, plotId?: number | null) => {
      const training = exam?.training?.["@id"];
      const chukkers_length = exam?.training?.chukkers?.length || 0;
      const order_index = chukkers_length;

      if (training) {
        try {
          //Check if created Chukker is on top of other chukker

          //User positions
          let chukker_start = data.manual_index_start;
          let chukker_end = data.manual_index_end;

          //Find if its in chukker
          let isChukkerInRange = isChukkerInRage(
            chukker_start,
            chukker_end,
            chukkers,
          );

          if (isChukkerInRange) {
            toast(t("chukkers:overlapChukker"), { type: "error" });
            return;
          }

          let tempChukker = plotId
            ? chukkers.filter((chukker) => chukker.id !== plotId)
            : chukkers;

          const newChukkers = [
            ...tempChukker.map((chukker, i) => ({
              ...chukker,
              order_index: i,
              training,
            })),
            {
              ...data,
              training,
              order_index,
            },
          ];

          await updateExamChukkers(newChukkers);

          //Reset Chukkers state
          handleChukkerEditMode(false);
          handleChukkerEdition(false);
          handleChukkerSelection(false);
          handleSetEditActive(false);
          handlePlotBandId(null);
        } catch (error) {
          toast(t("error:generic"), { type: "error" });
        }
      }
    },
    [chukkers, exam, id, queryClient, t],
  );

  const onDeleteChukker = useCallback(
    async (deleteId: number) => {
      const training = exam?.training?.["@id"];

      if (training) {
        try {
          const filteredChukkers = chukkers
            .filter((chukker) => chukker.id !== deleteId)
            .map((chukker, i) => ({
              ...chukker,
              order_index: i,
              training,
            }));

          const newChukkersResponse = await saveExamChukker(filteredChukkers);

          setChukkers(newChukkersResponse);
          queryClient.refetchQueries(["exam", toNumber(id)]);
        } catch (error) {
          toast(t("error:generic"), { type: "error" });
        }
      }
    },
    [chukkers, exam, id, queryClient, t],
  );

  const handleChukkerEditMode = useCallback((value: boolean) => {
    setIsChukkerEditModeEnabled(value);
  }, []);

  const handleChukkerEdition = useCallback((value: boolean) => {
    setIsChukkerEditionEnabled(value);
  }, []);

  const handleChukkerSelection = useCallback((value: boolean) => {
    setIsChukkerSelectionEnabled(value);
  }, []);

  const handleSetEditActive = useCallback((value: boolean) => {
    setIsEditActive(value);
  }, []);

  const handlePlotBandId = useCallback((value: number | null) => {
    setPlotBandId(value);
  }, []);

  //If Ecg is not retrieved
  //Handle if its Loaded from app we sent event else we redirect to Kb Page
  const handleEcgNotRetrieved = () => {
    window.open(
      "https://knowledgebase.equimetre.com/fr/comment-t%C3%A9l%C3%A9charger-un-ecg",
      "_blank",
    );
  };

  const totalTime = () => {
    let value = 0;

    if (isEmpty(time)) {
      value = 0;
    } else {
      let _time = startTimeFrom0(time);

      value = last(_time) as number;
    }

    return getTime(value);
  };

  const isPostProcessActive =
    !isEmpty(
      exam?.training.post_processing_info?.next_post_processing_request,
    ) ||
    !exam?.training?.moments_transposed_file_url ||
    !exam?.training?.raw_moments_transposed_file_url;

  // Rendering Logic Start here

  if (isExamLoading) {
    return <TrackViewSkeleton></TrackViewSkeleton>;
  }

  if (examHasError) {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"calc(100vh - 200px)"}
      >
        <NotFound></NotFound>

        {/* Leave Page Dialog on restricted Mode */}
        <Dialog
          open={blocker.state === "blocked"}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{t("leave:title")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("leave:subtitle")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t("alert.cancel")}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={disconnectUser}
              autoFocus
            >
              {t("button:bluetooth:disconnect")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Page title={getPageTitle()}>
      <Banner
        exam={exam?.training}
        macAdress={exam?.training?.equimetre}
        horse={exam?.training?.horse_trainer?.horse}
        trainer={exam?.training?.horse_trainer.trainer}
        open={showBanner && isAdmin}
        handleClose={() => setShowBanner(false)}
      ></Banner>
      <Header
        boxProps={{
          justifyContent: { md: "flex-start", xs: "center" },
          alignItems: { md: "center", xs: "center" },
          width: { md: "auto", xs: "100%" },
        }}
        titleProps={{
          ...(restrictedMode
            ? {}
            : {
                component: Link,
                target: "_blank",
                to: routes.DASHBOARD_ROUTES.HORSE_VIEW.replace(
                  ":id",
                  String(exam?.training?.horse_trainer?.horse?.id),
                ),
              }),
        }}
        leftChildren={
          <img
            style={{ borderRadius: "50%", alignSelf: "center" }}
            src={exam?.training?.horse_trainer?.horse?.avatar_url}
            alt="horse profile"
            width={80}
            onError={(e) => {
              e.currentTarget.src =
                "https://follyfarmstud.co.uk/wp-content/uploads/2020/04/folly-placeholder-horse-300x300.png";
            }}
            height={80}
          />
        }
        text={
          <Box display={"flex"} alignItems={"center"} gap={0.5}>
            {upperCase(
              exam?.training?.horse_trainer?.horse?.name ??
                t("navbar:myTrainings"),
            )}
            {showShareLink && (
              <Tooltip
                title={t("training:shareWithToken")}
                arrow
                placement="right"
              >
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    getShareLink();
                  }}
                >
                  <Icon icon="material-symbols-light:share" color="lightGray" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        date={`${formatDate(
          exam?.training?.creation_date ?? "",
          "dddd Do MMM YYYY - LT",
          currentLang as any,
        )
          .toLowerCase()
          // Capitalize first letter of each word (including accented letters)
          .replace(/(^|\s)\p{L}/gu, (match) => match.toUpperCase())
          // Fix AM/PM
          .replace("Am", "AM")
          .replace("Pm", "PM")
          // Remove ordinal º
          .replace("º", "")}`}
        dateStyle={{
          fontSize: "15px",
        }}
      >
        {!restrictedMode && (
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={1}
            flexWrap={{ md: "nowrap", xs: "wrap" }}
          >
            {/* Hide the edit Button in restricted mode or demo mode  */}

            {!restrictedMode && exam?.training?.type !== "demotraining" && (
              <Button
                onClick={handleEdit}
                color="warning"
                startIcon={<Edit></Edit>}
                disableElevation
                id="edit-training-button"
                variant="outlined"
              >
                {t("firstSection:editTraining")}
              </Button>
            )}

            {/* Reprocess Data button */}
            {processEnabled && (
              <LoadingButton
                loading={processExamMutation.isLoading}
                onClick={processData}
                startIcon={<Icon icon="fluent-mdl2:processing"></Icon>}
                variant="contained"
              >
                {t("process:button")}
              </LoadingButton>
            )}

            {/* Pdf Split button */}
            <SplitButton
              disabled={isPdfDownloading || isExamExporting}
              selectedIndex={selectedPdfOptionIndex}
              onIndexChange={(idx) => {
                setSelectedPdfOptionIndex(idx);
                handlePdfBtnClick(handleOptionPdfClick(idx));
              }}
              options={pdfOptions}
              onClick={handlePdfBtnClick}
            />
            {isExamHasEcg ? (
              <EcgActionsModal
                handleEcgBtnClick={handleEcgBtnClick}
                ecgDownloading={isEcgDownloading}
                exam={exam}
              />
            ) : (
              <Button
                variant={"contained"}
                onClick={() =>
                  handleDownloadWithAppSynchro(
                    "FETCH_ECG_FROM_EQUIMETRE_BUTTON_CLICKED",
                    () => handleEcgNotRetrieved(),
                  )
                }
                startIcon={<Icon icon="streamline:help-chat-2"></Icon>}
              >
                {t("ecg:notRetrieved")}
              </Button>
            )}
          </Box>
        )}
      </Header>
      <Divider sx={{ my: 2 }}></Divider>

      {/* Post Process Banner */}
      <PostProcess
        isActive={isPostProcessActive}
        onRefresh={reloadPostProcess}
        isReloading={examQuery.isFetching}
      />

      <InformationSection
        pitchTemperature={
          isUserTypePolo ? exam?.training?.pitch_temperature : null
        }
        humidity={isUserTypePolo ? exam?.training?.pitch_humidity : null}
        restrictedMode={restrictedMode}
        examId={exam?.training?.id}
        horse={exam?.training?.horse_trainer?.horse ?? null}
        rider={exam?.training?.rider?.name ?? null}
        intensity={exam?.training?.intensity ?? 0}
        duration={toNumber(exam?.training?.insight?.duration ?? 0)}
        trainingType={exam?.training?.training_type?.display_name ?? null}
        weather={exam?.training?.weather}
        horseShoe={exam?.training?.horse_shoeing?.display_name}
        track_name_auto={exam?.training?.track_name_automatic?.display_name}
        track_surface_auto={
          exam?.training?.track_surface_automatic?.display_name
        }
        training_type_auto={
          exam?.training?.training_type_automatic?.display_name
        }
        track={{
          id: exam?.training?.track_name?.id,
          name:
            exam?.training?.track_name?.name ??
            exam?.training?.track_name_automatic?.name,
          trackCondition: exam?.training?.track_condition?.display_name,
          trackSurfaceKey:
            exam?.training?.track_surface?.name ??
            exam?.training?.track_surface_automatic?.name,
          trackSurfaceName:
            exam?.training?.track_surface?.display_name ??
            exam?.training?.track_surface_automatic?.display_name,
        }}
        insight={exam?.training?.insight}
        isExamHasEcg={isExamHasEcg}
        device={
          exam?.training?.equimetre?.display_name ??
          exam?.training?.equimetre?.mac_address?.mac_address
        }
        date={formatDate(
          exam?.training?.creation_date ?? "",
          "ddd MMM DD YYYY - LT",
        )}
        comment={exam?.training?.comment}
        onEdit={handleEdit}
      ></InformationSection>
      <Box my={3}></Box>
      <Hideable isVisible={userSettings?.display_section_key_parameters}>
        <KeyParamSection
          restrictedMode={restrictedMode}
          keyParams={exam?.computed_key_parameters}
        ></KeyParamSection>
      </Hideable>

      <Box my={3}></Box>
      <DataSuccessMessage message={exam?.training?.flash_comment} />
      <Box my={3}></Box>
      <Hideable isVisible={userSettings?.display_section_averages}>
        <GaitAndHrZone
          horseId={exam?.training?.horse_trainer?.horse?.id}
          maxHr={exam?.hr_max}
          maxHrRest={exam?.training?.horse_computed_heart_rate_at_rest}
          data={exam?.averages_and_hr_zones}
          totalDistance={convertSpeed(
            initialSysUnit as "metric" | "imperial",
            last(rawDistance) as number,
          )}
          totalTime={totalTime()}
        ></GaitAndHrZone>
      </Hideable>
      <Box my={3}></Box>
      <Hideable isVisible={userSettings?.display_section_map}>
        {isMapLoaded ? (
          <MapSection
            enabled
            setDistance={(value) => setDistance(value ?? "")}
            transposedFile={exam?.training?.moments_transposed_file_url}
            chronology={exam?.averages_and_hr_zones?.gait_chronology}
            flIndex={finish_line?.index_moment ?? null}
            finishLine={finish_line}
            checkPoints={filteredCheckPoints}
            onIndexChange={(value) => setCurrentindex(value)}
            setIsLoading={(value) => setLoading(value)}
            isFinishLineActive={isFinishLineActive}
            enableCheckbox={false}
            showSave={false}
            speed={speed}
            bpm={bpm}
            distance={rawDistance}
            time={time}
            relevant_index_start={exam?.training?.relevant_moments_index_start}
            relevant_index_end={exam?.training?.relevant_moments_index_end}
            currentIndex={currentIndex}
            lats={lats}
            allure={allure}
            lngs={lngs}
            pace={strideFrequency}
            strideLength={strideLength}
            mapMode={mapMode}
            onMapModeChange={(mode) => setMapMode(mode)}
          ></MapSection>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="500px"
          >
            <CircularProgress />
          </Box>
        )}
      </Hideable>
      <Box my={3}></Box>

      {isUserTypePolo && (
        <ChukkersTable
          chukkers={chukkers || []}
          onSaveChukkers={updateExamChukkers}
          time={time}
          currentIndex={currentIndex}
          trainingId={exam?.training?.["@id"]}
        />
      )}

      <TabsGraphs
        showIntervalTraining={exam?.training?.is_interval_training ?? false}
        strideFrequency={strideFrequency}
        strideLength={strideLength}
        time={time}
        rawDistance={rawDistance}
        speed={speed}
        bpm={bpm}
        loCardio={loCardio}
        editable={false}
        distance={distance}
        scaleOption={scaleOption}
        onChangeScale={(val: Scale) => setScaleOption(val)}
        currentIndex={currentIndex}
        flIndex={finish_line?.index_moment ?? null}
        loading={loading}
        heats={exam?.training?.heats ?? []}
        isFinishLineActive={isFinishLineActive}
        intervals={exam?.training?.sectionals ?? []}
        checkPoints={exam?.training?.checkpoints ?? []}
        id={exam?.training?.id}
        percentageGoodCardio={exam?.training.percentage_good_cardio ?? 100}
        onIndexChange={(index) => setCurrentindex(index)}
        chukkers={isUserTypePolo ? chukkers : undefined}
        saveChukker={saveChukker}
        onDeleteChukker={onDeleteChukker}
        isChukkerEditModeEnabled={isChukkerEditModeEnabled}
        isChukkerEditionEnabled={isChukkerEditionEnabled}
        isChukkerSelectionEnabled={isChukkerSelectionEnabled}
        setIsChukkerEditModeEnabled={handleChukkerEditMode}
        setIsChukkerEditionEnabled={handleChukkerEdition}
        setIsChukkerSelectionEnabled={handleChukkerSelection}
        plotBandId={plotBandId}
        editActive={editActive}
        setIsEditActive={handleSetEditActive}
        setPlotBandId={handlePlotBandId}
      ></TabsGraphs>
      {isAdmin && (
        <Fab
          onClick={() => setShowBanner(!showBanner)}
          color="secondary"
          variant={"circular"}
          size="small"
          sx={{
            position: "fixed",
            bottom: showBanner ? 80 : 16,
            right: 12,
            transition: "bottom 0.1s ",
          }}
        >
          <AdminPanelSettings></AdminPanelSettings>
        </Fab>
      )}

      {/* Leave Page Dialog on restricted Mode */}
      <Dialog
        open={blocker.state === "blocked"}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("leave:title")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("leave:subtitle")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("alert.cancel")}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={disconnectUser}
            autoFocus
          >
            {t("button:bluetooth:disconnect")}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isChukkersLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Page>
  );
}

export default ExamView;
